// React imports
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

// Generic imports
import axios from 'axios';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

// MUI Imports
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

dayjs.extend(advancedFormat);

function ErrorEmails(props) {
    const { accesstoken } = useOutletContext();
    const [gmailStatus, setGmailStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorSummary, setErrorSummary] = useState(null);
    const [error, setError] = useState(null);
    const [codeContext, setCodeContext] = useState(null);
    const [codeContextLoading, setCodeContextLoading] = useState(false);
    const [codeContextDialogOpen, setCodeContextDialogOpen] = useState(false);
    const [errorStack, setErrorStack] = useState(null);
    const [currentStackFrame, setCurrentStackFrame] = useState(0);
    const [rawStackTrace, setRawStackTrace] = useState(null);
    const [showRawStack, setShowRawStack] = useState(false);
    
    const demoOrgId = '251f5e5d-e272-47e8-beb2-d905c9d05546';
    const sessionId = window.localStorage.getItem('admin-google-session-id') || uuidv4();
    
    useEffect(() => {
        window.localStorage.setItem('admin-google-session-id', sessionId);
        checkGmailConnection();
    }, []);

    async function checkGmailConnection() {
        try {
            const { data } = await axios.get(`/api/gmail?organizationId=${demoOrgId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);
            setGmailStatus(data);
        } catch (e) {
            console.error('Error checking Gmail connection:', e);
            setGmailStatus({ status: 'not-connected' });
        }
    }

    async function handleGmailLogin() {
        setError(null);
        
        // Load Google API
        if (!window.gapi) {
            await new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://apis.google.com/js/api.js';
                script.onload = () => {
                    window.gapi.load('auth2', resolve);
                };
                script.onerror = reject;
                document.head.appendChild(script);
            });
        }

        // Initialize auth2 if needed
        if (!window.gapi.auth2) {
            await window.gapi.auth2.init({
                client_id: '605788172223-qp2dppdlf9iqns2tkvj4jshiq0evm8db.apps.googleusercontent.com',
            });
        }

        // Create or get the code client
        if (!window.gmailCodeClient) {
            window.gmailCodeClient = {
                callback: null,
                requestCode: function() {
                    const authInstance = window.gapi.auth2.getAuthInstance();
                    authInstance.grantOfflineAccess({
                        scope: 'https://www.googleapis.com/auth/gmail.readonly',
                        prompt: 'consent'
                    }).then((resp) => {
                        if (this.callback) {
                            this.callback(resp);
                        }
                    }).catch((err) => {
                        if (this.callback) {
                            this.callback({ error: err });
                        }
                    });
                }
            };
        }

        window.gmailCodeClient.callback = async (resp) => {
            if (resp.error !== undefined) {
                setError(resp.error_description || resp.error);
                return;
            }

            const { code } = resp;
            try {
                await axios.get(`/api/gmail/login?googleCode=${encodeURIComponent(code)}&organizationId=${demoOrgId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);
                await checkGmailConnection();
            } catch (e) {
                setError('Failed to connect to Gmail: ' + (e.response?.data?.detail || e.message));
            }
        };

        window.gmailCodeClient.requestCode();
    }

    async function fetchErrorEmails() {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(`/api/admin/error-emails?accesstoken=${accesstoken}&sessionId=${sessionId}`);
            setErrorSummary(data);
        } catch (e) {
            setError('Failed to fetch error emails: ' + (e.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }

    /**
     * Parse stack trace to extract all frames
     */
    function parseStackFrames(stack) {
        if (!stack) return [];
        
        const lines = stack.split('\n');
        const frames = [];
        
        for (const line of lines) {
            // Match format: "at functionName (file:line:character)" or "at file:line:character"
            const match = line.match(/at\s+(?:[^(]+)?\s*\(([^)]+):(\d+):(\d+)\)/) || line.match(/at\s+([^:]+):(\d+):(\d+)/);
            if (match) {
                const file = match[1];
                const lineNum = parseInt(match[2], 10);
                const charNum = parseInt(match[3], 10);
                
                // Extract function name if available
                const functionMatch = line.match(/at\s+([^(]+)\s*\(/);
                const functionName = functionMatch ? functionMatch[1].trim() : '';
                
                frames.push({
                    file,
                    line: lineNum,
                    character: charNum,
                    functionName,
                    rawLine: line.trim(),
                });
            }
        }
        
        return frames;
    }

    async function fetchCodeContext(filePath, character, errorEntry = null) {
        console.log('fetchCodeContext called with:', { filePath, character, errorEntry });
        setCodeContextLoading(true);
        setCodeContextDialogOpen(true);
        setCodeContext(null);
        setCurrentStackFrame(0);
        
        let frames = null;
        let initialFilePath = filePath;
        let initialCharacter = character;
        
        // Parse stack trace if error entry is provided
        if (errorEntry && errorEntry.errors && errorEntry.errors.length > 0) {
            const firstError = errorEntry.errors[0];
            if (firstError.stack) {
                frames = parseStackFrames(firstError.stack);
                setErrorStack(frames);
                setRawStackTrace(firstError.stack);
                
                // Start from the first frame (index 0) if we have frames
                if (frames.length > 0) {
                    initialFilePath = frames[0].file;
                    initialCharacter = frames[0].character;
                }
            } else {
                setErrorStack(null);
                setRawStackTrace(null);
            }
        } else {
            setErrorStack(null);
            setRawStackTrace(null);
        }
        setShowRawStack(false);
        
        try {
            const { data } = await axios.get(`/api/admin/error-code-context?accesstoken=${accesstoken}&filePath=${encodeURIComponent(initialFilePath)}&character=${initialCharacter}`);
            console.log('Code context response:', data);
            setCodeContext(data);
        } catch (e) {
            console.error('Error fetching code context:', e);
            setCodeContext({ error: e.response?.data?.detail || e.message });
        } finally {
            setCodeContextLoading(false);
        }
    }

    async function navigateToStackFrame(frameIndex) {
        if (!errorStack || frameIndex < 0 || frameIndex >= errorStack.length) return;
        
        const frame = errorStack[frameIndex];
        setCurrentStackFrame(frameIndex);
        await fetchCodeContext(frame.file, frame.character, null); // Don't re-parse stack
    }

    return (
        <>
            {gmailStatus?.status === 'not-connected' || gmailStatus?.status === 'expired' ? (
                <Box sx={{ m: 2 }}>
                    <Typography variant="h4" sx={{ mb: 2 }}>Error Email Analyzer</Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Please connect your Gmail account to analyze error emails.
                    </Alert>
                    <Button variant="contained" onClick={handleGmailLogin}>
                        Connect to Gmail
                    </Button>
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </Box>
            ) : gmailStatus?.status === 'connected' ? (
                <Box sx={{ m: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h4">Error Email Analyzer</Typography>
                        <Button 
                            variant="contained" 
                            onClick={fetchErrorEmails}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Scan Error Emails'}
                        </Button>
                    </Box>
                    
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Connected to Gmail as {gmailStatus.email}
                    </Alert>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {errorSummary && (
                        <ErrorSummaryTable 
                            summary={errorSummary.summary || errorSummary} 
                            errorsByDate={errorSummary.errorsByDate}
                            onViewCode={(filePath, character, errorEntry) => fetchCodeContext(filePath, character, errorEntry)}
                        />
                    )}
                </Box>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                </Box>
            )}

            <Dialog 
                open={codeContextDialogOpen} 
                onClose={() => setCodeContextDialogOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    Code Context
                    <IconButton
                        aria-label="close"
                        onClick={() => setCodeContextDialogOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {errorStack && errorStack.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6">Error Stack Trace</Typography>
                                {rawStackTrace && (
                                    <Button 
                                        size="small" 
                                        onClick={() => setShowRawStack(!showRawStack)}
                                    >
                                        {showRawStack ? 'Hide' : 'Show'} Raw Stack
                                    </Button>
                                )}
                            </Box>
                            {showRawStack && rawStackTrace ? (
                                <Paper sx={{ p: 2, bgcolor: '#f5f5f5', maxHeight: '200px', overflow: 'auto' }}>
                                    <Typography 
                                        component="pre" 
                                        sx={{ 
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            margin: 0,
                                        }}
                                    >
                                        {rawStackTrace}
                                    </Typography>
                                </Paper>
                            ) : (
                                <Paper sx={{ p: 2, bgcolor: '#f5f5f5', maxHeight: '200px', overflow: 'auto' }}>
                                    {errorStack.map((frame, index) => (
                                        <Box
                                            key={index}
                                            onClick={() => navigateToStackFrame(index)}
                                            sx={{
                                                p: 1,
                                                mb: 0.5,
                                                cursor: 'pointer',
                                                borderRadius: 1,
                                                bgcolor: index === currentStackFrame ? '#e3f2fd' : 'transparent',
                                                '&:hover': {
                                                    bgcolor: index === currentStackFrame ? '#e3f2fd' : '#fafafa',
                                                },
                                                border: index === currentStackFrame ? '2px solid #2196f3' : '1px solid transparent',
                                            }}
                                        >
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.75rem',
                                                    color: index === currentStackFrame ? '#1976d2' : 'text.primary',
                                                    fontWeight: index === currentStackFrame ? 'bold' : 'normal',
                                                }}
                                            >
                                                {frame.functionName && (
                                                    <span style={{ color: '#666' }}>{frame.functionName}</span>
                                                )}
                                                {frame.functionName && ' '}
                                                <span style={{ color: '#d32f2f' }}>
                                                    {frame.file.split('/').pop()}:{frame.line}:{frame.character}
                                                </span>
                                            </Typography>
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.7rem',
                                                    color: 'text.secondary',
                                                    display: 'block',
                                                    mt: 0.5,
                                                }}
                                            >
                                                {frame.rawLine}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Paper>
                            )}
                        </Box>
                    )}
                    
                    {codeContextLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : codeContext?.error ? (
                        <Alert severity="error">{codeContext.error}</Alert>
                    ) : codeContext && codeContext.code ? (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                File: {codeContext.filePath}<br />
                                Line: {codeContext.lineNumber}<br />
                                Character: {codeContext.errorChar} (showing {codeContext.startChar}-{codeContext.endChar})
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: '#f5f5f5', overflow: 'auto', maxHeight: '60vh' }}>
                                <Typography 
                                    component="pre" 
                                    sx={{ 
                                        fontFamily: 'monospace', 
                                        fontSize: '0.875rem',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        margin: 0,
                                    }}
                                >
                                    {codeContext.code && codeContext.code.length >= 500 ? (
                                        <>
                                            {codeContext.code.substring(0, 500)}
                                            <span style={{ 
                                                backgroundColor: '#ffebee', 
                                                borderLeft: '3px solid #d32f2f',
                                                paddingLeft: '2px',
                                                position: 'relative',
                                                display: 'inline-block',
                                            }}>
                                                <span style={{
                                                    position: 'absolute',
                                                    left: '-2px',
                                                    top: '-14px',
                                                    fontSize: '0.65rem',
                                                    color: '#d32f2f',
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#ffebee',
                                                    padding: '1px 4px',
                                                    borderRadius: '2px',
                                                    whiteSpace: 'nowrap',
                                                    zIndex: 1,
                                                }}>
                                                    ← ERROR
                                                </span>
                                            </span>
                                            {codeContext.code.substring(500)}
                                        </>
                                    ) : (
                                        codeContext.code
                                    )}
                                </Typography>
                            </Paper>
                        </Box>
                    ) : codeContext ? (
                        <Box>
                            <Typography>No code available in response</Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                                Response keys: {Object.keys(codeContext).join(', ')}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                {JSON.stringify(codeContext, null, 2)}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography>Waiting for code context...</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {errorStack && errorStack.length > 1 && (
                        <>
                            <Button 
                                onClick={() => navigateToStackFrame(currentStackFrame - 1)}
                                disabled={currentStackFrame === 0}
                            >
                                Previous Frame
                            </Button>
                            <Typography variant="body2" sx={{ mx: 2 }}>
                                Frame {currentStackFrame + 1} of {errorStack.length}
                            </Typography>
                            <Button 
                                onClick={() => navigateToStackFrame(currentStackFrame + 1)}
                                disabled={currentStackFrame === errorStack.length - 1}
                            >
                                Next Frame
                            </Button>
                        </>
                    )}
                    <Button onClick={() => setCodeContextDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

function ErrorSummaryTable({ summary, errorsByDate, onViewCode }) {
    const totalErrors = summary.reduce((sum, e) => sum + e.count, 0);
    
    // Prepare chart data
    const chartData = errorsByDate && errorsByDate.length > 0 ? errorsByDate.map(item => ({
        date: dayjs(item.date).format('MMMM Do'),
        count: item.count,
        fullDate: item.date,
    })) : [];
    
    return (
        <Box>
            {chartData.length > 0 && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Errors Over Time</Typography>
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        <LineChart
                            width={Math.max(800, chartData.length * 60)}
                            height={300}
                            series={[
                                {
                                    data: chartData.map(d => d.count),
                                    label: 'Errors',
                                    color: '#d32f2f',
                                },
                            ]}
                            xAxis={[{
                                scaleType: 'point',
                                data: chartData.map(d => d.date),
                            }]}
                            yAxis={[{
                                label: 'Number of Errors',
                            }]}
                        />
                    </Box>
                </Paper>
            )}
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Unique Error Locations
                            </Typography>
                            <Typography variant="h4">
                                {summary.length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Errors
                            </Typography>
                            <Typography variant="h4">
                                {totalErrors}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Most Frequent Error
                            </Typography>
                            <Typography variant="h4">
                                {summary.length > 0 ? summary[0].count : 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Location</strong></TableCell>
                            <TableCell align="right"><strong>Count</strong></TableCell>
                            <TableCell><strong>Version</strong></TableCell>
                            <TableCell><strong>Top Reason</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {summary.map((entry, index) => {
                            const location = `${entry.location.file}:${entry.location.line}:${entry.location.character}`;
                            const topReason = Array.from(entry.reasons)[0] || 'N/A';
                            const version = entry.latestVersion === true ? 'Latest version' : entry.latestVersion === false ? 'Old version' : 'unknown';
                            const versionColor = version === 'Latest version' ? 'success' : version === 'Old version' ? 'warning' : 'default';
                            const canViewCode = entry.location?.fullPath && entry.location?.character && onViewCode;
                            return (
                                <TableRow 
                                    key={index} 
                                    hover
                                    onClick={() => {
                                        if (canViewCode) {
                                            console.log('Viewing code for:', entry.location.fullPath, entry.location.character);
                                            onViewCode(entry.location.fullPath, entry.location.character, entry);
                                        } else {
                                            console.log('Cannot view code:', { 
                                                fullPath: entry.location?.fullPath, 
                                                character: entry.location?.character, 
                                                onViewCode: !!onViewCode 
                                            });
                                        }
                                    }}
                                    sx={{ cursor: canViewCode ? 'pointer' : 'default' }}
                                >
                                    <TableCell 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (canViewCode) {
                                                onViewCode(entry.location.fullPath, entry.location.character, entry);
                                            }
                                        }}
                                        sx={{ cursor: canViewCode ? 'pointer' : 'default' }}
                                    >
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                            {location}
                                        </Typography>
                                    </TableCell>
                                    <TableCell 
                                        align="right"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (canViewCode) {
                                                onViewCode(entry.location.fullPath, entry.location.character, entry);
                                            }
                                        }}
                                        sx={{ cursor: canViewCode ? 'pointer' : 'default' }}
                                    >
                                        <Chip label={entry.count} color="error" size="small" />
                                    </TableCell>
                                    <TableCell
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (canViewCode) {
                                                onViewCode(entry.location.fullPath, entry.location.character, entry);
                                            }
                                        }}
                                        sx={{ cursor: canViewCode ? 'pointer' : 'default' }}
                                    >
                                        <Chip 
                                            label={version} 
                                            color={versionColor} 
                                            size="small"
                                            sx={{ textTransform: 'none' }}
                                        />
                                    </TableCell>
                                    <TableCell
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (canViewCode) {
                                                onViewCode(entry.location.fullPath, entry.location.character, entry);
                                            }
                                        }}
                                        sx={{ cursor: canViewCode ? 'pointer' : 'default' }}
                                    >
                                        <Typography variant="body2" sx={{ maxWidth: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={topReason}>
                                            {topReason}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ mt: 3 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>Detailed View (Top 10 Errors)</Typography>
                {summary.slice(0, 10).map((entry, index) => {
                    const location = `${entry.location.file}:${entry.location.line}:${entry.location.character}`;
                    const version = entry.latestVersion === true ? 'Latest version' : entry.latestVersion === false ? 'Old version' : 'unknown';
                    const versionColor = version === 'Latest version' ? 'success' : version === 'Old version' ? 'warning' : 'default';
                    return (
                        <Accordion key={index}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography sx={{ fontWeight: 'bold' }}>{location}</Typography>
                                {entry.latestVersion !== undefined && (
                                    <Chip 
                                        label={version} 
                                        color={versionColor} 
                                        size="small"
                                        sx={{ ml: 2, textTransform: 'none' }}
                                    />
                                )}
                                <Typography sx={{ ml: 2, color: 'text.secondary' }}>
                                    ({entry.count} occurrences)
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1, fontFamily: 'monospace', color: 'text.secondary' }}>
                                        <strong>Full Path:</strong> {entry.location.fullPath}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Reasons:</strong>
                                    </Typography>
                                    <Box sx={{ mb: 2 }}>
                                        {Array.from(entry.reasons).map((reason, i) => (
                                            <Chip key={i} label={reason} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                        ))}
                                    </Box>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Organizations:</strong>
                                    </Typography>
                                    <Box sx={{ mb: 2 }}>
                                        {Array.from(entry.orgs).map((org, i) => (
                                            <Chip key={i} label={org} size="small" color="primary" sx={{ mr: 0.5, mb: 0.5 }} />
                                        ))}
                                    </Box>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Affected Users:</strong>
                                    </Typography>
                                    <Box sx={{ mb: 2 }}>
                                        {Array.from(entry.users).slice(0, 10).map((user, i) => (
                                            <Chip key={i} label={user} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                        ))}
                                        {entry.users.size > 10 && (
                                            <Chip label={`+${entry.users.size - 10} more`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                        )}
                                    </Box>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Sample Paths:</strong>
                                    </Typography>
                                    <Box>
                                        {Array.from(entry.paths).slice(0, 5).map((path, i) => (
                                            <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mb: 0.5 }}>
                                                {path}
                                            </Typography>
                                        ))}
                                        {entry.paths.size > 5 && (
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                                +{entry.paths.size - 5} more paths
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        </Box>
    );
}

export default ErrorEmails;

